/**
 * Panel primitives — left/right collapsible side panels + center content.
 * On mobile (<768px) panels render as collapsible accordion sections below center.
 */

import { useState, type ReactNode } from "react";

interface ThreeColumnProps {
  left?:           ReactNode;
  center:          ReactNode;
  right?:          ReactNode;
  leftCollapsed:   boolean;
  rightCollapsed:  boolean;
  leftWidth?:      number;
  rightWidth?:     number;
  isMobile?:       boolean;
  leftLabel?:      string;
  rightLabel?:     string;
}

export function ThreeColumn({
  left, center, right,
  leftCollapsed, rightCollapsed,
  leftWidth  = 240,
  rightWidth = 280,
  isMobile   = false,
  leftLabel  = "Controls",
  rightLabel = "Effects",
}: ThreeColumnProps) {
  const [leftAccordionOpen, setLeftAccordionOpen] = useState(false);
  const [rightAccordionOpen, setRightAccordionOpen] = useState(false);

  if (isMobile) {
    return (
      <div style={{
        flex: 1,
        overflow: "hidden auto",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overscrollBehavior: "contain",
      }}>
        {/* Center — full width */}
        <div style={{ flex: "1 0 auto", minHeight: "40vh", background: "var(--bg-base)" }}>
          {center}
        </div>

        {/* Left accordion */}
        {left && !leftCollapsed && (
          <MobileAccordion
            label={leftLabel}
            open={leftAccordionOpen}
            onToggle={() => setLeftAccordionOpen(v => !v)}
          >
            {left}
          </MobileAccordion>
        )}

        {/* Right accordion */}
        {right && !rightCollapsed && (
          <MobileAccordion
            label={rightLabel}
            open={rightAccordionOpen}
            onToggle={() => setRightAccordionOpen(v => !v)}
          >
            {right}
          </MobileAccordion>
        )}
      </div>
    );
  }

  return (
    <div style={{
      display: "flex",
      flex: 1,
      overflow: "hidden",
      height: "100%",
    }}>
      {/* Left panel */}
      {left && (
        <div style={{
          width: leftCollapsed ? 0 : leftWidth,
          minWidth: leftCollapsed ? 0 : leftWidth,
          overflow: "hidden",
          transition: `width var(--duration-slow) var(--ease-out),
                       min-width var(--duration-slow) var(--ease-out)`,
          borderRight: leftCollapsed ? "none" : "1px solid var(--border-subtle)",
          background: "var(--sidebar-bg)",
          flexShrink: 0,
        }}>
          <div style={{ width: leftWidth, height: "100%", overflow: "hidden auto" }}>
            {left}
          </div>
        </div>
      )}

      {/* Center — always dominant */}
      <div style={{ flex: 1, overflow: "hidden", background: "var(--bg-base)" }}>
        {center}
      </div>

      {/* Right panel */}
      {right && (
        <div style={{
          width: rightCollapsed ? 0 : rightWidth,
          minWidth: rightCollapsed ? 0 : rightWidth,
          overflow: "hidden",
          transition: `width var(--duration-slow) var(--ease-out),
                       min-width var(--duration-slow) var(--ease-out)`,
          borderLeft: rightCollapsed ? "none" : "1px solid var(--border-subtle)",
          background: "var(--sidebar-bg)",
          flexShrink: 0,
        }}>
          <div style={{ width: rightWidth, height: "100%", overflow: "hidden auto" }}>
            {right}
          </div>
        </div>
      )}
    </div>
  );
}

/** Collapsible accordion section for mobile layout */
function MobileAccordion({ label, open, onToggle, children }: {
  label: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const contentId = `accordion-${label.toLowerCase().replace(/\s+/g, '-')}-content`;
  
  return (
    <div style={{
      borderTop: "1px solid var(--border-subtle)",
      background: "var(--sidebar-bg)",
    }}>
      <button
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={contentId}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "var(--space-4)",
          background: "none",
          border: "none",
          cursor: "pointer",
          fontFamily: "var(--font-sans)",
          fontSize: "var(--text-sm)",
          fontWeight: "var(--weight-semibold)",
          color: "var(--text-secondary)",
          minHeight: 44,
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <span style={{ textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "var(--text-xs)" }}>
          {label}
        </span>
        <span style={{
          transition: "transform var(--duration-fast) var(--ease-out)",
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          fontSize: 10,
          color: "var(--text-muted)",
        }}>
          ▼
        </span>
      </button>
      {open && (
        <div id={contentId} role="region" aria-labelledby={`accordion-${label.toLowerCase().replace(/\s+/g, '-')}-button`} style={{ padding: "0 var(--space-3) var(--space-4)" }}>
          {children}
        </div>
      )}
    </div>
  );
}

/** Section header in a side panel */
export function PanelSection({ label, children, action }: {
  label: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div style={{ padding: "var(--space-4) var(--space-3) var(--space-2)" }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: "var(--space-3)",
      }}>
        <span style={{
          fontSize: "var(--text-xs)",
          fontWeight: "var(--weight-semibold)",
          color: "var(--text-muted)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}>
          {label}
        </span>
        {action}
      </div>
      {children}
    </div>
  );
}

/** Collapsible effect card for the right panel */
export function EffectCard({ title, enabled, onToggle, children, defaultOpen = true }: {
  title: string;
  enabled: boolean;
  onToggle: () => void;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  return (
    <div style={{
      margin: "var(--space-3)",
      borderRadius: "var(--radius-lg)",
      border: `1px solid ${enabled ? "var(--border-default)" : "var(--border-subtle)"}`,
      background: enabled ? "var(--bg-overlay)" : "var(--bg-raised)",
      overflow: "hidden",
      transition: "border-color var(--duration-fast)",
    }}>
      {/* Card header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "var(--space-3) var(--space-4)",
        borderBottom: enabled && defaultOpen ? "1px solid var(--border-subtle)" : "none",
      }}>
        <span style={{
          fontSize: "var(--text-sm)",
          fontWeight: "var(--weight-semibold)",
          color: enabled ? "var(--text-primary)" : "var(--text-muted)",
        }}>
          {title}
        </span>
        <TogglePill enabled={enabled} onToggle={onToggle} />
      </div>

      {/* Card body */}
      {enabled && (
        <div style={{ padding: "var(--space-3) var(--space-4) var(--space-4)" }}>
          {children}
        </div>
      )}
    </div>
  );
}

/** iOS-style toggle pill */
export function TogglePill({ enabled, onToggle, size = "sm" }: {
  enabled: boolean;
  onToggle: () => void;
  size?: "sm" | "md";
}) {
  const w = size === "sm" ? 40 : 48;
  const h = size === "sm" ? 24 : 28;
  const d = size === "sm" ? 18 : 22;
  return (
    <div
      onClick={e => { e.stopPropagation(); onToggle(); }}
      style={{
        width: w, height: h,
        borderRadius: "var(--radius-full)",
        background: enabled ? "var(--accent)" : "var(--bg-floating)",
        border: `1px solid ${enabled ? "var(--accent)" : "var(--border-strong)"}`,
        cursor: "pointer",
        position: "relative",
        transition: "background var(--duration-fast), border-color var(--duration-fast)",
        flexShrink: 0,
      }}
    >
      <div style={{
        position: "absolute",
        top: (h - d) / 2 - 1,
        left: enabled ? w - d - 2 : 2,
        width: d, height: d,
        borderRadius: "var(--radius-full)",
        background: enabled ? "#000" : "var(--text-muted)",
        transition: "left var(--duration-fast) var(--ease-spring)",
      }} />
    </div>
  );
}

/** Labeled slider */
export function EffectSlider({ label, value, min, max, onChange, unit = "%" }: {
  label: string; value: number; min: number; max: number;
  onChange: (v: number) => void; unit?: string;
}) {
  return (
    <div style={{ marginBottom: "var(--space-3)" }}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: "var(--space-2)",
      }}>
        <span style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>{label}</span>
        <span style={{
          fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)",
          fontWeight: "var(--weight-semibold)", color: "var(--text-primary)",
          minWidth: 32, textAlign: "right",
        }}>
          {value}{unit}
        </span>
      </div>
      <div style={{ position: "relative", height: 4, borderRadius: "var(--radius-full)" }}>
        {/* Track */}
        <div style={{
          position: "absolute", inset: 0,
          borderRadius: "var(--radius-full)",
          background: "var(--bg-floating)",
        }} />
        {/* Fill */}
        <div style={{
          position: "absolute", top: 0, left: 0, bottom: 0,
          width: `${((value - min) / (max - min)) * 100}%`,
          borderRadius: "var(--radius-full)",
          background: "var(--accent)",
          transition: "width var(--duration-instant)",
        }} />
        {/* Native input (transparent, for interaction) */}
        <input
          type="range" min={min} max={max} value={value}
          onChange={e => onChange(Number(e.target.value))}
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            opacity: 0, cursor: "pointer", margin: 0,
          }}
        />
      </div>
    </div>
  );
}

/** Icon button */
export function IconButton({ icon, label, onClick, variant = "ghost", active = false, danger = false }: {
  icon: string; label: string; onClick: () => void;
  variant?: "ghost" | "solid" | "outline";
  active?: boolean; danger?: boolean;
}) {
  const bg = danger
    ? (active ? "var(--status-error)" : "transparent")
    : (active ? "var(--accent)" : (variant === "solid" ? "var(--bg-elevated)" : "transparent"));
  const color = danger
    ? (active ? "#fff" : "var(--status-error)")
    : (active ? "#000" : "var(--text-secondary)");

  return (
    <button
      title={label}
      onClick={onClick}
      style={{
        width: 44, height: 44,
        display: "flex", alignItems: "center", justifyContent: "center",
        borderRadius: "var(--radius-lg)",
        border: variant === "outline" ? "1px solid var(--border-default)" : "none",
        background: bg,
        color,
        fontSize: 16,
        cursor: "pointer",
        fontFamily: "var(--font-sans)",
        transition: "all var(--duration-fast) var(--ease-out)",
        flexShrink: 0,
      }}
    >
      {icon}
    </button>
  );
}
